import { useState } from 'react';
import { Flex, Badge, Button, DropdownMenu } from '@radix-ui/themes';
import {
  Pencil1Icon,
  PlusIcon,
  TrashIcon,
  DotsHorizontalIcon,
} from '@radix-ui/react-icons';

import {
  Fingerprint,
  TransactionFingerprint,
  LogFingerprint,
  BlockFingerprint,
  SignalType,
} from '../../types';

import { ConditionDisplay } from '@/components';
import TransactionFingerprintEditor, {
  transactionAttributes,
} from './TransactionFingerprintEditor';
import LogFingerprintEditor, { logAttributes } from './LogFingerprintEditor';
import BlockFingerprintEditor, {
  blockAttributes,
} from './BlockFingerprintEditor';

interface FingerprintItemProps {
  fingerprint: Fingerprint;
  signalType: SignalType;
  onEdit: (fingerprint: Fingerprint) => void;
  onDelete: () => void;
}

const FingerprintItem = ({
  fingerprint,
  signalType,
  onEdit,
  onDelete,
}: FingerprintItemProps) => {
  const [isEditing, setIsEditing] = useState(false);

  // Get the label based on signal type
  const getFieldLabel = () => {
    if (signalType === SignalType.Transaction) {
      const fp = fingerprint as TransactionFingerprint;
      const attr = transactionAttributes.find(
        (a) => a.key.toLowerCase() === fp.txField?.toLowerCase(),
      );
      return attr?.label || fp.txField || fingerprint.field;
    } else if (signalType === SignalType.Log) {
      const fp = fingerprint as LogFingerprint;
      const attr = logAttributes.find((a) => a.key === fp.field);
      return attr?.label || fp.field;
    } else if (signalType === SignalType.Block) {
      const fp = fingerprint as BlockFingerprint;
      const attr = blockAttributes.find((a) => a.key === fp.field);
      return attr?.label || fp.field;
    }
    return fingerprint.field;
  };

  const handleEditSave = (updatedFingerprint: Fingerprint) => {
    onEdit(updatedFingerprint);
    setIsEditing(false);
  };

  if (isEditing) {
    if (signalType === SignalType.Transaction) {
      return (
        <TransactionFingerprintEditor
          initialFingerprint={fingerprint as TransactionFingerprint}
          onSave={(fp) => handleEditSave(fp as Fingerprint)}
          onCancel={() => setIsEditing(false)}
        />
      );
    } else if (signalType === SignalType.Log) {
      return (
        <LogFingerprintEditor
          initialFingerprint={fingerprint as LogFingerprint}
          onSave={(fp) => handleEditSave(fp as Fingerprint)}
          onCancel={() => setIsEditing(false)}
        />
      );
    } else if (signalType === SignalType.Block) {
      return (
        <BlockFingerprintEditor
          initialFingerprint={fingerprint as BlockFingerprint}
          onSave={(fp) => handleEditSave(fp as Fingerprint)}
          onCancel={() => setIsEditing(false)}
        />
      );
    }
  }

  return (
    <Flex
      direction="row"
      align="center"
      gap="4"
      justify="between"
      p="2"
      style={{
        backgroundColor: 'var(--gray-3)',
        borderRadius: '0.25rem',
        padding: '0.75rem',
        border: '1px solid var(--gray-6)',
      }}
    >
      <Flex direction="row" gap="2" align="center">
        <Badge
          variant="surface"
          size="1"
          color="gray"
          style={{ fontFamily: 'monospace' }}
        >
          {getFieldLabel()}
        </Badge>
        <ConditionDisplay
          operation={fingerprint.operation}
          value={fingerprint.value}
        />
      </Flex>
      <DropdownMenu.Root>
        <DropdownMenu.Trigger>
          <Button variant="ghost" size="1" m="1">
            <DotsHorizontalIcon width={12} height={12} />
          </Button>
        </DropdownMenu.Trigger>
        <DropdownMenu.Content sideOffset={5}>
          <DropdownMenu.Item onClick={() => setIsEditing(true)}>
            <Pencil1Icon width={16} height={16} />
            Edit condition
          </DropdownMenu.Item>
          <DropdownMenu.Item onClick={onDelete} color="red">
            <TrashIcon width={16} height={16} />
            Delete condition
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Root>
    </Flex>
  );
};

interface AddConditionButtonProps {
  signalType: SignalType;
  onSave: (fingerprint: Fingerprint) => void;
}

const AddConditionButton = ({
  signalType,
  onSave,
}: AddConditionButtonProps) => {
  const [isCreating, setIsCreating] = useState<boolean>(false);

  const handleSave = (fingerprint: Fingerprint) => {
    onSave(fingerprint);
    setIsCreating(false);
  };

  if (isCreating) {
    if (signalType === SignalType.Transaction) {
      return (
        <TransactionFingerprintEditor
          onSave={(fp) => handleSave(fp as Fingerprint)}
          onCancel={() => setIsCreating(false)}
        />
      );
    } else if (signalType === SignalType.Log) {
      return (
        <LogFingerprintEditor
          onSave={(fp) => handleSave(fp as Fingerprint)}
          onCancel={() => setIsCreating(false)}
        />
      );
    } else if (signalType === SignalType.Block) {
      return (
        <BlockFingerprintEditor
          onSave={(fp) => handleSave(fp as Fingerprint)}
          onCancel={() => setIsCreating(false)}
        />
      );
    }
    // Network doesn't have fingerprints
    return null;
  }

  // Only show add button for signal types that support fingerprints
  if (
    signalType !== SignalType.Transaction &&
    signalType !== SignalType.Log &&
    signalType !== SignalType.Block
  ) {
    return null;
  }

  return (
    <Button
      variant="ghost"
      size="1"
      color="gray"
      style={{
        border: '1px dashed var(--gray-7)',
        borderRadius: '0.5rem',
        fontWeight: 500,
        width: '100%',
        margin: 0,
        boxSizing: 'border-box',
      }}
      onClick={() => setIsCreating(true)}
    >
      <PlusIcon width={16} height={16} />
      Add Condition
    </Button>
  );
};

export interface FingerprintListProps {
  fingerprints: Fingerprint[];
  signalType: SignalType;
  onAdd: (fingerprint: Fingerprint) => void;
  onEdit: (index: number, fingerprint: Fingerprint) => void;
  onDelete: (index: number) => void;
}

export const FingerprintList = ({
  fingerprints,
  signalType,
  onAdd,
  onEdit,
  onDelete,
}: FingerprintListProps) => {
  return (
    <Flex direction="column" gap="2">
      {fingerprints.map((fingerprint, index) => (
        <FingerprintItem
          key={index}
          fingerprint={fingerprint}
          signalType={signalType}
          onEdit={(updated) => onEdit(index, updated)}
          onDelete={() => onDelete(index)}
        />
      ))}
      <AddConditionButton signalType={signalType} onSave={onAdd} />
    </Flex>
  );
};
